import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Toolbar, IconButton, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link,
  Code
} from '@mui/icons-material';
import Loader from '../../../Loader';

interface HTMLEditorProps {
  initialValue?: string | null;
  loading: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const EditorWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  minHeight: '150px',
  '& .editor-toolbar': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[50],
  },
  '& .editor-content': {
    padding: theme.spacing(0.75),
    minHeight: '100px',
    fontSize: '18px',
    lineHeight: 1.2,
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '8px',
      '& td, & th': {
        padding: '4px',
        border: '1px solid #ccc',
        textAlign: 'left',
        verticalAlign: 'top',
      },
      '& th': {
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
      },
    },
    '& p': {
      margin: '0 0 0.25em 0',
      fontSize: '18px',
      lineHeight: 1.2,
    },
    '& ul, & ol': {
      fontSize: '18px',
      paddingLeft: '18px',
      margin: '0.25em 0',
      '& li': {
        marginBottom: '0.25em',
      },
    },
  },
}));

const HTMLEditor: React.FC<HTMLEditorProps> = (props) => {
  const { initialValue, loading, onChange, onBlur } = props;
  const [content, setContent] = useState(initialValue || '');
  const editorRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const previousInitialValueRef = useRef<string | null | undefined>(undefined);

  // Save and restore cursor position
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  };

  const restoreCursorPosition = (position: number) => {
    if (!editorRef.current) return;
    
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT
    );

    let currentPosition = 0;
    let node;
    
    while (node = walker.nextNode()) {
      const nodeLength = node.textContent?.length || 0;
      if (currentPosition + nodeLength >= position) {
        const range = document.createRange();
        const selection = window.getSelection();
        const offset = position - currentPosition;
        
        range.setStart(node, Math.min(offset, nodeLength));
        range.collapse(true);
        
        selection?.removeAllRanges();
        selection?.addRange(range);
        break;
      }
      currentPosition += nodeLength;
    }
  };

  // Update content from initialValue when it changes
  useEffect(() => {
    // Check if initialValue has actually changed (by value, not reference)
    const currentValue = initialValue || '';
    const previousValue = previousInitialValueRef.current || '';
    const hasValueChanged = currentValue !== previousValue;
    
    // Update the ref for next comparison
    previousInitialValueRef.current = initialValue;

    // Only update if:
    // 1. Value actually changed (not just reference)
    // 2. We're not currently updating from user input
    if (!hasValueChanged || isUpdatingRef.current) {
      return;
    }

    // Use requestAnimationFrame to ensure editorRef is ready after DOM updates
    let retryCount = 0;
    const maxRetries = 10;
    
    const updateEditor = () => {
      if (!editorRef.current) {
        // If ref isn't ready yet, try again (with max retries to prevent infinite loop)
        if (retryCount < maxRetries) {
          retryCount++;
          requestAnimationFrame(updateEditor);
        }
        return;
      }

      // Normalize empty strings for comparison
      const isPreviousEmpty = !previousValue || previousValue.trim() === '';
      const isCurrentEmpty = !currentValue || currentValue.trim() === '';

      // First time initialization
      if (!isInitializedRef.current && !isCurrentEmpty) {
        editorRef.current.innerHTML = currentValue;
        setContent(currentValue);
        isInitializedRef.current = true;
        return;
      }

      // Only update if value actually changed (external change, like template selection)
      // Don't update if user is typing
      if (hasValueChanged && !isUpdatingRef.current) {
        // Save cursor position if editor has focus
        const hadFocus = document.activeElement === editorRef.current;
        const cursorPosition = hadFocus ? saveCursorPosition() : 0;
        
        editorRef.current.innerHTML = currentValue;
        setContent(currentValue);
        
        // Restore cursor if editor had focus
        if (hadFocus && cursorPosition > 0) {
          requestAnimationFrame(() => {
            restoreCursorPosition(cursorPosition);
          });
        }
      }
    };

    requestAnimationFrame(updateEditor);
  }, [initialValue]);

  const handleContentChange = () => {
    if (editorRef.current && !isUpdatingRef.current) {
      const newContent = editorRef.current.innerHTML;
      isUpdatingRef.current = true;
      setContent(newContent);
      onChange(newContent);
      // Reset the flag after a delay to account for React batching
      // Use a slightly longer timeout to ensure parent re-renders complete
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const execCommand = (command: string, value?: string) => {
    const cursorPosition = saveCursorPosition();
    document.execCommand(command, false, value);
    
    // Restore focus and cursor position after command
    setTimeout(() => {
      editorRef.current?.focus();
      restoreCursorPosition(cursorPosition);
      handleContentChange();
    }, 10);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle common keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <EditorWrapper>
      <Toolbar className="editor-toolbar" variant="dense">
        <IconButton
          size="small"
          onClick={() => execCommand('bold')}
          title="Bold (Ctrl+B)"
        >
          <FormatBold />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('italic')}
          title="Italic (Ctrl+I)"
        >
          <FormatItalic />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('underline')}
          title="Underline (Ctrl+U)"
        >
          <FormatUnderlined />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <IconButton
          size="small"
          onClick={() => execCommand('insertUnorderedList')}
          title="Bullet List"
        >
          <FormatListBulleted />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('insertOrderedList')}
          title="Numbered List"
        >
          <FormatListNumbered />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <IconButton
          size="small"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          title="Insert Link"
        >
          <Link />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('formatBlock', 'pre')}
          title="Code Block"
        >
          <Code />
        </IconButton>
      </Toolbar>
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleContentChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </EditorWrapper>
  );
};

export default HTMLEditor;
